import {
  CircularProgress,
  Switch,
  Button,
  Typography,
  Box,
  Chip,
} from '@mui/material'
import QRCode from 'react-qr-code'
import {
  ErrorMessage,
  Info,
  MainContainer,
  ContentContainer,
  QRCodeContainer,
  SwitchContainer,
  ModeToggleContainer,
  ToggleOption,
  CompanySelectionContainer,
  ValidityInfo,
  ValidationLabel,
} from './InviteRep.styles'
import { TInviteRepProps } from './InviteRep.types'
import { useInviteRepLogic } from './InviteRep.logic'
import { CompanySearchBar } from 'components/CompanySearchBar'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import RefreshIcon from '@mui/icons-material/Refresh'

const InviteRep: React.FC<TInviteRepProps> = (props) => {
  const {
    error,
    loading,
    navigationUrl,
    regenerateCode,
    timeRemaining,
    codeGenerated,
    formatTime,
    isExpired,
    state,
    handleSwitchChange,
    handleCompanySelect,
    handleCopyLink,
    showCopyLinkButtonForInviteRep,
    toggleRepType,
  } = useInviteRepLogic(props)

  if (error) {
    return <ErrorMessage>{error}</ErrorMessage>
  }

  return (
    <MainContainer>
      <ModeToggleContainer>
        <ToggleOption
          $isActive={!state.isGuestRep}
          onClick={() => state.isGuestRep && toggleRepType()}
        >
          Regular RN
        </ToggleOption>
        <Switch
          checked={state.isGuestRep}
          onChange={toggleRepType}
          color="primary"
        />
        <ToggleOption
          $isActive={state.isGuestRep}
          onClick={() => !state.isGuestRep && toggleRepType()}
        >
          Guest RN
        </ToggleOption>
      </ModeToggleContainer>

      <ContentContainer>
        {state.isGuestRep ? (
          // Guest Rep Content
          <>
            <CompanySelectionContainer>
              {!state.selectedCompany ? (
                <Typography variant="h3">Select Hospital of the RN</Typography>
              ) : null}
              <CompanySearchBar
                companiesIds={props.assetsCompanies}
                handleChangeFormData={handleCompanySelect}
                label="Select Company"
                placeholder="Search and select company"
                required
              />
            </CompanySelectionContainer>

            {state.selectedCompany && (
              <>
                <SwitchContainer>
                  <Typography>Allow RN to add products?</Typography>
                  <Switch
                    checked={state.canAddAsset}
                    onChange={handleSwitchChange}
                  />
                </SwitchContainer>

                {codeGenerated && (
                  <Box display="flex" gap={1} mb={1}>
                    <Chip
                      icon={<AccessTimeIcon />}
                      label={`Valid for: ${formatTime(timeRemaining)}`}
                      color={timeRemaining > 30 ? 'primary' : 'warning'}
                      variant="outlined"
                    />
                  </Box>
                )}

                <QRCodeContainer elevation={3}>
                  {loading ? (
                    <CircularProgress />
                  ) : (
                    <>
                      <QRCode
                        fgColor="var(--primary)"
                        value={navigationUrl}
                        size={256}
                        level="M"
                        style={{ opacity: isExpired ? 0.3 : 1 }}
                      />
                      {isExpired && (
                        <ValidationLabel color="error">
                          <WarningAmberIcon fontSize="small" />
                          <Typography variant="caption">
                            QR code expired
                          </Typography>
                        </ValidationLabel>
                      )}
                    </>
                  )}
                </QRCodeContainer>

                {isExpired && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<RefreshIcon />}
                    onClick={regenerateCode}
                    sx={{ mt: 2 }}
                  >
                    Regenerate QR Code
                  </Button>
                )}

                <ValidityInfo>
                  <Typography variant="body2">
                    An RN can capture this code to approve the list of products.
                  </Typography>
                </ValidityInfo>
                <ValidityInfo>
                  <Typography variant="body2">
                    Each QR code is valid for 5 minutes after generation. It is
                    also single-use only — once scanned, it cannot be reused.
                  </Typography>
                </ValidityInfo>
              </>
            )}
          </>
        ) : (
          // Regular Rep Content
          <>
            <SwitchContainer>
              Allow RN to add products?
              <Switch
                checked={state.canAddAsset}
                onChange={handleSwitchChange}
              />
            </SwitchContainer>
            <QRCodeContainer>
              {loading ? (
                <CircularProgress />
              ) : (
                <QRCode
                  fgColor="var(--primary)"
                  value={navigationUrl}
                  size={256}
                />
              )}
            </QRCodeContainer>
            <Info>
              An RN can capture this code to approve the list of assets.
            </Info>
          </>
        )}

        {/* Common button for both modes */}
        {showCopyLinkButtonForInviteRep && navigationUrl && !loading && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleCopyLink}
            disabled={
              !navigationUrl || loading || (state.isGuestRep && isExpired)
            }
            sx={{ mt: 1 }}
          >
            Copy Link
          </Button>
        )}
      </ContentContainer>
    </MainContainer>
  )
}

export default InviteRep
